/**
 * @fileoverview Smooth scroll to in-page anchors with an offset from [vdx-scroll-offset].
 * Offset can be the element's height or a CSS-like value: px, rem, %, vh, vw.
 */

import { logWarn, showErrorOnElement } from './errors.js';

const MODULE = 'scrolloffset';

/** Selector for the element that defines the scroll offset (height or vdx-scroll-offset value). */
const SCROLL_OFFSET_SELECTOR = '[vdx-scroll-offset]';

/** Attribute for a CSS-like offset value (e.g. "100px", "5rem", "10vh"). If absent, element height is used. */
const ATTR_OFFSET = 'vdx-scroll-offset';

/**
 * Disables Webflow's built-in smooth scrolling so native behavior: 'smooth' is used.
 * Safe to call multiple times.
 */
function disableWebflowSmoothScroll() {
    if (typeof window === 'undefined') return;
    try {
        window.Webflow = window.Webflow || [];
        window.Webflow.push(function () {
            if (typeof window.$ === 'function') {
                window.$(document).off('click.wf-scroll');
            }
        });
    } catch (_) {
        // Webflow/jQuery not available
    }
}

/**
 * Parses the offset value (string or number) into px. Uses element height for %, and document/viewport for units.
 * @param {string | number} offset - Value from attribute or element height.
 * @param {Element | null} element - Element with [vdx-scroll-offset]; used for % and for error display.
 * @returns {number} Offset in pixels (0 if invalid).
 */
function parseOffsetPx(offset, element) {
    const el = element ?? (typeof document !== 'undefined' ? document.querySelector(SCROLL_OFFSET_SELECTOR) : null);
    const elementHeight = el && typeof el.getBoundingClientRect === 'function' ? el.getBoundingClientRect().height : 0;

    if (typeof offset === 'number' && !Number.isNaN(offset)) {
        return Math.max(0, offset);
    }
    if (typeof offset !== 'string' || !offset.trim()) {
        return Math.max(0, elementHeight);
    }

    const raw = offset.trim();
    const baseFontSize = typeof document !== 'undefined' && document.documentElement
        ? parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
        : 16;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 0;
    const vw = typeof window !== 'undefined' ? window.innerWidth : 0;

    let value = 0;
    if (raw.endsWith('px')) {
        value = parseFloat(raw.replace('px', '').trim());
    } else if (raw.endsWith('rem')) {
        value = parseFloat(raw.replace('rem', '').trim()) * baseFontSize;
    } else if (raw.endsWith('%')) {
        value = (parseFloat(raw.replace('%', '').trim()) / 100) * elementHeight;
    } else if (raw.endsWith('vh')) {
        value = (parseFloat(raw.replace('vh', '').trim()) / 100) * vh;
    } else if (raw.endsWith('vw')) {
        value = (parseFloat(raw.replace('vw', '').trim()) / 100) * vw;
    } else {
        value = parseFloat(raw);
    }

    if (Number.isNaN(value)) {
        const msg = `vdx-scroll-offset value must be a string (e.g. "100px", "50%", "2rem"). Got: "${raw}".`;
        logWarn(msg, { module: MODULE, context: { offset: raw } });
        if (el) showErrorOnElement(el, msg);
        return Math.max(0, elementHeight);
    }
    return Math.max(0, value);
}

/**
 * Returns the current scroll offset in px for the [vdx-scroll-offset] element (attribute or height).
 * @returns {{ offsetPx: number, element: Element | null }}
 */
function getOffset(element) {
    if (typeof document === 'undefined') return { offsetPx: 0, element: null };
    const el = element ?? document.querySelector(SCROLL_OFFSET_SELECTOR);
    if (!el) return { offsetPx: 0, element: null };
    const height = el.getBoundingClientRect?.()?.height ?? 0;
    const attr = el.getAttribute(ATTR_OFFSET);
    const hasValue = typeof attr === 'string' && attr.trim() !== '';
    const offsetPx = parseOffsetPx(hasValue ? attr : height, el);
    return { offsetPx, element: el };
}

/**
 * Scrolls the window to the target element with the given offset (native smooth).
 * @param {Element} target - Anchor target element.
 * @param {number} offsetPx - Offset in px (e.g. navbar height).
 */
function scrollToTarget(target, offsetPx) {
    if (typeof window === 'undefined' || !target || typeof target.getBoundingClientRect !== 'function') return;
    const top = target.getBoundingClientRect().top + window.scrollY - offsetPx;
    window.scrollTo({ top, behavior: 'smooth' });
}

/**
 * If the URL has a hash, scrolls to that element with the current offset.
 * @param {number} offsetPx
 */
function scrollToHash(offsetPx) {
    if (typeof window === 'undefined' || !window.location?.hash) return;
    const hash = window.location.hash;
    if (hash === '#') return;
    const target = document.querySelector(hash);
    if (target) scrollToTarget(target, offsetPx);
}

/**
 * Handles click on an anchor with href="#id": prevents default and smooth-scrolls with offset.
 * @param {Event} event
 * @param {Element | null} offsetElement - Element with [vdx-scroll-offset]; used to compute offset.
 */
function handleAnchorClick(event, offsetElement) {
    const link = event.target.closest('a[href^="#"]:not([href="#"])');
    if (!link) return;
    const href = link.getAttribute('href');
    if (!href || href === '#') return;
    const target = document.querySelector(href);
    if (!target) return;
    event.preventDefault();
    const { offsetPx } = getOffset(offsetElement);
    scrollToTarget(target, offsetPx);
    if (typeof window.history?.pushState === 'function') {
        window.history.pushState(null, '', href);
    }
}

/**
 * Initializes smooth scroll: wires document click and hashchange to scroll with [vdx-scroll-offset] offset.
 * No-op if no [vdx-scroll-offset] element. Safe to call multiple times (guarded).
 */
export function addSmoothScroll() {
    if (typeof document === 'undefined') return;
    if (window.vdxScrollOffsetInitialized) return;

    const offsetEl = document.querySelector(SCROLL_OFFSET_SELECTOR);
    if (!offsetEl) return;

    window.vdxScrollOffsetInitialized = true;
    disableWebflowSmoothScroll();

    const { offsetPx } = getOffset(offsetEl);

    document.addEventListener('click', (e) => handleAnchorClick(e, offsetEl));
    window.addEventListener('hashchange', () => scrollToHash(getOffset(offsetEl).offsetPx));

    scrollToHash(offsetPx);
}
